import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  resolveCliStrategyHealthFindingTarget,
  type CliStrategyHealthFinding,
  type CliStrategyHealthReport,
} from '@modules/shared-types';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';

export interface StrategyHealthReportViewProps {
  report: CliStrategyHealthReport;
  variant?: 'detail' | 'canvas' | 'compact';
  maxFindingsPerGroup?: number;
  onFindingGo?: (finding: CliStrategyHealthFinding) => void;
  onFindingRepair?: (finding: CliStrategyHealthFinding) => void;
}

type FindingGroup = { key: string; findings: CliStrategyHealthFinding[]; total: number };

function severityColor(severity: string): 'error' | 'warning' | 'info' | 'default' {
  if (severity === 'error' || severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  if (severity === 'info' || severity === 'advisory') return 'info';
  return 'default';
}

function groupFindings(
  report: CliStrategyHealthReport,
  by: 'check' | 'severity',
): FindingGroup[] {
  const groups = new Map<string, CliStrategyHealthFinding[]>();
  for (const finding of report.findings) {
    const key = by === 'check' ? finding.checkId : finding.severity;
    groups.set(key, [...(groups.get(key) ?? []), finding]);
  }
  const totalOf = (key: string, findings: CliStrategyHealthFinding[]) => (
    by === 'check'
      ? report.summary.byCheck[key] ?? findings.length
      : report.summary.bySeverity[key] ?? findings.length
  );
  return [...groups.entries()]
    .map(([key, findings]) => ({ key, findings, total: totalOf(key, findings) }))
    .sort((left, right) => right.total - left.total || left.key.localeCompare(right.key));
}

const FindingRow: React.FC<{
  finding: CliStrategyHealthFinding;
  showCheckId: boolean;
  onGo?: (finding: CliStrategyHealthFinding) => void;
  onRepair?: (finding: CliStrategyHealthFinding) => void;
}> = ({ finding, showCheckId, onGo, onRepair }) => {
  const target = resolveCliStrategyHealthFindingTarget(finding);
  return (
  <Box
    sx={{
      py: 0.75,
      borderTop: '1px solid',
      borderColor: 'divider',
      '&:first-of-type': { borderTop: 0 },
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      <Chip size="small" variant="outlined" color={severityColor(finding.severity)} label={finding.severity} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {showCheckId && (
          <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
            {finding.checkId}
          </Typography>
        )}
        <Typography variant="body2">{finding.message}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          {target.screenId && <Chip size="small" variant="outlined" label={`screen ${target.screenId}`} sx={{ fontFamily: 'monospace' }} />}
          {target.blockKey && <Chip size="small" variant="outlined" label={`block ${target.blockKey}`} sx={{ fontFamily: 'monospace' }} />}
          {finding.variableKey && <Chip size="small" variant="outlined" label={`variable ${finding.variableKey}`} sx={{ fontFamily: 'monospace' }} />}
        </Stack>
      </Box>
      {onGo && (target.blockKey || target.screenId) && (
        <Button
          size="small"
          onClick={() => onGo(finding)}
          data-testid={TASKS_PANEL.healthFindingGo}
          sx={{ minWidth: 'auto', textTransform: 'none' }}
        >
          Go →
        </Button>
      )}
      {onRepair && target.blockKey && (
        <Button
          size="small"
          color="warning"
          onClick={() => onRepair(finding)}
          data-testid={TASKS_PANEL.healthFindingRepair}
          sx={{ minWidth: 'auto', textTransform: 'none' }}
        >
          Repair
        </Button>
      )}
    </Stack>
  </Box>
  );
};

const RouterPathTrace: React.FC<{ report: CliStrategyHealthReport }> = ({ report }) => {
  const trace = report.routerPathTrace;
  if (!trace) return null;
  return (
    <Box
      sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      data-testid={TASKS_PANEL.strategyHealthPathTrace}
    >
      <Box sx={{ px: 1.25, py: 1, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2">router.pathTrace</Typography>
        <Typography variant="caption" color="text.secondary">
          {trace.distinctPaths} distinct path{trace.distinctPaths === 1 ? '' : 's'}
          {trace.goalsScreenId ? ` · goals screen ${trace.goalsScreenId}` : ''}
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Goal</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Terminal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trace.goals.map((goal, index) => {
            const status = String(goal.status ?? 'unknown');
            const successful = status === 'conditional-match';
            return (
              <TableRow key={`${String(goal.taskId ?? goal.optionValue ?? 'goal')}-${index}`}>
                <TableCell>{String(goal.optionValue ?? goal.taskId ?? `Goal ${index + 1}`)}</TableCell>
                <TableCell>
                  <Chip size="small" color={successful ? 'success' : 'warning'} variant="outlined" label={status} />
                </TableCell>
                <TableCell>{String(goal.terminal ?? goal.lastScreenId ?? '—')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export const StrategyHealthReportView: React.FC<StrategyHealthReportViewProps> = ({
  report,
  variant = 'detail',
  maxFindingsPerGroup = 3,
  onFindingGo,
  onFindingRepair,
}) => {
  const grouping = variant === 'canvas' ? 'severity' : 'check';
  const groups = useMemo(() => groupFindings(report, grouping), [grouping, report]);
  const errors = (report.summary.bySeverity.error ?? 0) + (report.summary.bySeverity.critical ?? 0);
  const warnings = report.summary.bySeverity.warning ?? 0;
  const hidden = Math.max(0, report.summary.findingsTotal - report.summary.emittedFindings);

  return (
    <Box data-testid={TASKS_PANEL.strategyHealthReportSection}>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ flexWrap: 'wrap', gap: 0.75, mb: report.summary.findingsTotal > 0 ? 1 : 0 }}
        data-testid={TASKS_PANEL.strategyHealthSummary}
      >
        <Chip
          size="small"
          color={errors > 0 ? 'error' : report.summary.findingsTotal > 0 ? 'warning' : 'success'}
          label={report.summary.findingsTotal > 0 ? `${report.summary.findingsTotal} findings` : '✓ 0 findings'}
        />
        {errors > 0 && <Chip size="small" variant="outlined" color="error" label={`${errors} errors`} />}
        {warnings > 0 && <Chip size="small" variant="outlined" color="warning" label={`${warnings} warnings`} />}
        {variant === 'detail' && Object.entries(report.summary.byCheck).map(([checkId, count]) => (
          <Chip key={checkId} size="small" variant="outlined" label={`${checkId} ${count}`} sx={{ fontFamily: 'monospace' }} />
        ))}
      </Stack>

      {report.summary.findingsTotal === 0 ? (
        <Alert severity="success" variant="outlined">
          No strategy health findings. {Object.entries(report.diagnosticsSummary).slice(0, 3).map(([key, value]) => `${value} ${key}`).join(' · ')}
        </Alert>
      ) : (
        <>
          {hidden > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1 }} data-testid={TASKS_PANEL.strategyHealthTruncationNote}>
              {hidden} more findings are not shown — summary counts are complete.
            </Alert>
          )}
          <Stack spacing={1}>
            {groups.map((group) => {
              const shown = group.findings.slice(0, maxFindingsPerGroup);
              const content = (
                <Box sx={{ px: variant === 'canvas' ? 0 : 1.25, pb: 0.5 }}>
                  {shown.map((finding, index) => (
                    <FindingRow
                      key={`${finding.checkId}-${finding.kind}-${finding.screenId ?? finding.blockKey ?? index}`}
                      finding={finding}
                      showCheckId={grouping === 'severity'}
                      onGo={onFindingGo}
                      onRepair={onFindingRepair}
                    />
                  ))}
                  {group.total > shown.length && (
                    <Typography variant="caption" color="text.secondary">
                      … {group.total - shown.length} more · counts are complete
                    </Typography>
                  )}
                </Box>
              );
              if (variant === 'canvas') {
                const severity = group.key;
                return (
                  <Accordion
                    key={group.key}
                    defaultExpanded={severity === 'error' || severity === 'critical'}
                    disableGutters
                    data-testid={TASKS_PANEL.healthSeverityGroup(severity)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ flex: 1, textTransform: 'capitalize' }}>{severity}</Typography>
                      <Chip size="small" color={severityColor(severity)} label={group.total} />
                    </AccordionSummary>
                    <AccordionDetails>{content}</AccordionDetails>
                  </Accordion>
                );
              }
              return (
                <Box
                  key={group.key}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                  data-testid={TASKS_PANEL.strategyHealthGroup(group.key)}
                >
                  <Stack direction="row" alignItems="center" sx={{ px: 1.25, py: 0.75, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ flex: 1, fontFamily: 'monospace' }}>{group.key}</Typography>
                    <Chip size="small" label={group.total} />
                  </Stack>
                  {content}
                </Box>
              );
            })}
          </Stack>
        </>
      )}
      {variant !== 'canvas' && <RouterPathTrace report={report} />}
    </Box>
  );
};
